import React, { useEffect, useMemo, useState } from "react";
import {
  Page,
  Card,
  Text,
  Button,
  InlineStack,
  BlockStack,
  Box,
  Badge,
  Modal,
  Icon,
} from "@shopify/polaris";
import {
  CreditCardIcon,
  HashtagIcon,
  HomeIcon,
  LocationIcon,
  OrderIcon,
  PackageIcon,
  PersonIcon,
} from "@shopify/polaris-icons";
import { isPendingReturnOrder } from "../utils/rentalStatus";
import LicenseExpiredOverlay from "../components/LicenseExpiredOverlay";

const FILTER_STATUS_OPTIONS = [
  { label: "All Statuses", value: "all" },
  { label: "Pending Fulfillment", value: "Reserved" },
  { label: "On Rent", value: "On Rent" },
  { label: "Return Requested", value: "Return Requested" },
  { label: "Closed", value: "Closed" },
];

const EDITABLE_STATUS_OPTIONS = [
  { label: "Pending Fulfillment", value: "Reserved" },
  { label: "On Rent", value: "On Rent" },
  { label: "Return Requested", value: "Return Requested" },
  { label: "Closed", value: "Return" },
];

function displayStatusLabel(status) {
  const value = String(status || "").trim();
  if (!value) return "Pending Fulfillment";
  if (value.toLowerCase() === "reserved") return "Pending Fulfillment";
  if (value.toLowerCase() === "return") return "Closed";
  return value;
}

function normalizeDateKey(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnly) {
    return `${dateOnly[1]}-${dateOnly[2]}-${dateOnly[3]}`;
  }

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getRentalStageLabel(order = {}) {
  const rawStatus = String(order.status || order.statusLabel || "").trim().toLowerCase();
  const startKey = normalizeDateKey(order.startDate);
  const endKey = normalizeDateKey(order.endDate);
  const todayKey = normalizeDateKey(new Date());

  if (rawStatus === "return requested") return "Return Requested";
  if (rawStatus === "return") return "Closed";
  if (rawStatus === "closed") return "Closed";
  if (rawStatus === "rejected") return "Rejected";
  if (rawStatus === "on rent") return "On Rent";
  if (startKey && endKey && todayKey && todayKey >= startKey && todayKey <= endKey) {
    return "On Rent";
  }
  if (endKey && todayKey && todayKey > endKey) return "Closed";
  if (startKey && todayKey && todayKey < startKey) return "Pending Fulfillment";
  if (rawStatus === "reserved") return "Pending Fulfillment";
  return displayStatusLabel(order.status || order.statusLabel || "Pending Fulfillment");
}

function getStatusTone(status) {
  if (status === "On Rent") return "attention";
  if (status === "Return Requested") return "warning";
  if (status === "Return" || status === "Closed") return "success";
  return "info";
}

function isApprovedReturnStatus(status = "") {
  const normalized = String(status || "").trim().toLowerCase();
  return normalized === "return" || normalized === "closed";
}

function getStatusHint(status = "") {
  if (String(status || "").trim() === "Return Requested") {
    return "Awaiting admin approval";
  }
  return "";
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  });
}

const TABLE_TEMPLATE = "1.2fr 2.25fr 1.25fr 0.9fr 1.45fr 140px";
const PAGE_SHELL_STYLE = {
  maxWidth: "1560px",
  margin: "0 auto",
  padding: "10px 16px 28px",
};
const DASHBOARD_STACK_STYLE = {
  display: "grid",
  gap: "20px",
};
const MODAL_CONTENT_STYLE = {
  display: "grid",
  gap: "12px",
  padding: "2px 0 6px",
};
const MODAL_SECTION_STYLE = {
  display: "grid",
  gap: "12px",
  padding: "14px 16px 16px",
  borderRadius: "18px",
  border: "1px solid #e5eaf2",
  background: "#ffffff",
  boxShadow: "0 3px 8px rgba(15, 23, 42, 0.035)",
};
const SECTION_HEADER_STYLE = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
};
const CUSTOMER_SECTION_HEADER_STYLE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
};
const MODAL_HEADER_TEXT_STYLE = {
  display: "grid",
  gap: "4px",
  minWidth: 0,
};
const MODAL_HEADER_META_STYLE = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  flexWrap: "wrap",
};
const MODAL_SECTION_VALUE_STYLE = {
  marginTop: "8px",
  color: "#17233f",
  fontSize: "17px",
  fontWeight: 700,
  lineHeight: 1.25,
  wordBreak: "break-word",
};
const MODAL_HEADER_ICON_WRAP_STYLE = {
  width: "46px",
  height: "46px",
  borderRadius: "14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};
const CUSTOMER_GRID_STYLE = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: "0",
  border: "1px solid #edf1f7",
  borderRadius: "16px",
  overflow: "hidden",
  background: "#fff",
};
const CUSTOMER_CELL_STYLE = {
  padding: "14px 16px 12px",
  minHeight: "78px",
};
const CUSTOMER_CELL_DIVIDER_STYLE = {
  borderLeft: "1px solid #edf1f7",
};
const RENTAL_ITEM_STYLE = {
  display: "grid",
  gap: "0",
  borderRadius: "16px",
  border: "1px solid #edf1f7",
  background: "#ffffff",
  overflow: "hidden",
};
const RENTAL_ITEM_HEAD_STYLE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  padding: "14px 16px",
};
const RENTAL_ITEM_META_STYLE = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  minWidth: 0,
};
const THUMB_STYLE = {
  width: "96px",
  height: "76px",
  borderRadius: "12px",
  background: "linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)",
  border: "1px solid #eef2f7",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#64748b",
  boxShadow: "0 4px 10px rgba(17, 24, 39, 0.05)",
  flexShrink: 0,
};
const THUMB_IMAGE_STYLE = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  borderRadius: "inherit",
  display: "block",
};
const RENTAL_TITLE_STYLE = {
  fontSize: "17px",
  fontWeight: 800,
  color: "#17233f",
  lineHeight: 1.25,
};
const RENTAL_ITEM_NUMBER_STYLE = {
  marginTop: "6px",
  fontSize: "13px",
  color: "#64748b",
};
const METRICS_GRID_STYLE = {
  display: "grid",
  gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
  gap: "0",
  borderTop: "1px solid #edf1f7",
  borderBottom: "1px solid #edf1f7",
  background: "#fff",
};
const METRIC_CELL_STYLE = {
  padding: "12px 14px",
  minHeight: "72px",
};
const METRIC_CELL_DIVIDER_STYLE = {
  borderLeft: "1px solid #edf1f7",
};
const PRICE_GRID_STYLE = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: "0",
  borderTop: "1px solid #edf1f7",
  background: "#fff",
};
const ADDRESS_GRID_STYLE = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: "12px",
};
const ADDRESS_BOX_STYLE = {
  padding: "14px 16px 16px",
  borderRadius: "18px",
  border: "1px solid #e7ebf3",
  background: "#ffffff",
  boxShadow: "0 3px 8px rgba(15, 23, 42, 0.035)",
};
const ADDRESS_LINES_STYLE = {
  display: "grid",
  gap: "12px",
};
const ADDRESS_LINE_STYLE = {
  display: "grid",
  gridTemplateColumns: "24px minmax(0, 1fr)",
  gap: "8px",
  alignItems: "start",
};
const ADDRESS_LINE_ICON_STYLE = {
  width: "24px",
  height: "24px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#5b657e",
  flexShrink: 0,
};
const HERO_STYLE = {
  padding: "28px 32px 24px",
  borderRadius: "18px",
  background: "linear-gradient(180deg, #0b1a39 0%, #0e2149 100%)",
  boxShadow: "0 18px 44px rgba(7, 15, 31, 0.18)",
  overflow: "hidden",
};
const HERO_INNER_STYLE = {
  display: "grid",
  gap: "22px",
  color: "#ffffff",
};
const HERO_TOP_STYLE = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "16px",
};
const HERO_COPY_STYLE = {
  display: "grid",
  gap: "6px",
};
const SUMMARY_GRID_STYLE = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: "18px",
};
const SUMMARY_TILE_STYLE = {
  padding: "22px 24px",
  borderRadius: "16px",
  background: "#ffffff",
  boxShadow: "0 8px 20px rgba(10, 18, 38, 0.08)",
};
const LIST_PANEL_STYLE = {
  background: "#ffffff",
  borderRadius: "16px",
  border: "1px solid rgba(15, 23, 42, 0.08)",
  boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
  overflow: "hidden",
};
const CARD_SHELL_STYLE = {
  borderRadius: "16px",
  boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
};
const LIST_HEADER_STYLE = {
  padding: "22px 26px 18px",
  borderBottom: "1px solid #e8edf3",
  background: "#ffffff",
};
const TABLE_HEADER_STYLE = {
  padding: "20px 26px 16px",
  background: "#ffffff",
};
const TABLE_ROW_STYLE = {
  padding: "22px 26px",
  borderBottom: "1px solid #e8edf3",
};
const CONTROL_BAR_STYLE = {
  background: "#ffffff",
  borderRadius: "14px",
  padding: "20px 20px",
  boxShadow: "0 8px 24px rgba(10, 18, 38, 0.08)",
};
const KPI_LABEL_STYLE = {
  color: "#5b657e",
  fontSize: "15px",
};
const KPI_VALUE_STYLE = {
  color: "#17233f",
};
const KPI_ICON_STYLE = {
  width: "70px",
  height: "70px",
  borderRadius: "18px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};
const KPI_META_STYLE = {
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  minWidth: 0,
};
const LIST_FOOTER_STYLE = {
  padding: "18px 26px 20px",
  background: "#ffffff",
};
const HERO_ACTION_BUTTON_STYLE = {
  appearance: "none",
  border: "1px solid rgba(255,255,255,0.26)",
  borderRadius: "12px",
  padding: "0 22px",
  minHeight: "56px",
  fontSize: "16px",
  fontWeight: 700,
  color: "#ffffff",
  background: "transparent",
  cursor: "pointer",
  boxShadow: "0 1px 0 rgba(255,255,255,0.08) inset",
};
const HERO_REFRESH_BUTTON_STYLE = {
  ...HERO_ACTION_BUTTON_STYLE,
  borderColor: "#2f74ff",
  background: "#2f74ff",
  boxShadow: "0 10px 24px rgba(47, 116, 255, 0.28)",
};
const METRIC_ARROW_STYLE = {
  width: "40px",
  height: "40px",
  borderRadius: "999px",
  background: "#f2f4f8",
  color: "#7c8798",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "18px",
  flexShrink: 0,
};

const HERO_CHIP_STYLE = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  padding: "8px 12px",
  borderRadius: "10px",
  fontSize: "14px",
  fontWeight: 700,
  lineHeight: 1,
};

const SEARCH_FIELD_STYLE = {
  minWidth: "0",
};

const FIELD_WRAPPER_STYLE = {
  display: "grid",
  alignItems: "center",
};

const CONTROL_INPUT_BASE_STYLE = {
  width: "100%",
  minHeight: "44px",
  borderRadius: "10px",
  border: "1px solid #aeb8c9",
  background: "#ffffff",
  color: "#17233f",
  fontSize: "15px",
  lineHeight: 1.2,
  padding: "0 14px",
  outline: "none",
  boxSizing: "border-box",
};

const CONTROL_TEXT_INPUT_STYLE = {
  ...CONTROL_INPUT_BASE_STYLE,
  paddingLeft: "44px",
};

const CONTROL_SELECT_STYLE = {
  ...CONTROL_INPUT_BASE_STYLE,
  paddingRight: "38px",
  appearance: "none",
  backgroundImage:
    "linear-gradient(45deg, transparent 50%, #6d7b95 50%), linear-gradient(135deg, #6d7b95 50%, transparent 50%)",
  backgroundPosition: "calc(100% - 18px) 18px, calc(100% - 12px) 18px",
  backgroundSize: "6px 6px, 6px 6px",
  backgroundRepeat: "no-repeat",
};

const FILTER_ROW_STYLE = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1.6fr) minmax(240px, 0.8fr) auto",
  gap: "14px",
  alignItems: "center",
};

const FILTER_META_STYLE = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  flexWrap: "wrap",
  justifyContent: "flex-start",
};

const TABLE_NOTE_STYLE = {
  color: "#66708a",
  fontSize: "14px",
  lineHeight: 1.35,
};

const SUCCESS_MESSAGE_STYLE = {
  color: "#d82c0d",
  fontSize: "14px",
  fontWeight: 600,
};

const TABLE_HEADING_STYLE = {
  color: "#17233f",
  fontSize: "16px",
  fontWeight: 700,
};

const ROW_PRIMARY_STYLE = {
  color: "#17233f",
  fontSize: "17px",
  fontWeight: 700,
  lineHeight: 1.2,
};

const ROW_SECONDARY_STYLE = {
  color: "#6c768b",
  fontSize: "14px",
  lineHeight: 1.25,
};

const TABLE_CELL_TEXT_STYLE = {
  color: "#20293f",
  fontSize: "16px",
};

const STATUS_PILL_BASE_STYLE = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: "88px",
  padding: "9px 14px",
  borderRadius: "10px",
  fontSize: "14px",
  fontWeight: 700,
  lineHeight: 1,
};

const TABLE_BUTTON_STYLE = {
  appearance: "none",
  border: "1px solid #ced7e6",
  background: "#ffffff",
  color: "#1a3f88",
  borderRadius: "10px",
  minHeight: "46px",
  padding: "0 18px",
  cursor: "pointer",
  fontSize: "15px",
  fontWeight: 700,
};

const ICON_BUTTON_STYLE = {
  appearance: "none",
  border: "1px solid #cfd6e4",
  background: "#ffffff",
  color: "#23314f",
  borderRadius: "10px",
  width: "64px",
  minHeight: "46px",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const FOOTER_BUTTON_STYLE = {
  appearance: "none",
  border: "1px solid #cfd7e5",
  background: "#ffffff",
  color: "#1c2a47",
  borderRadius: "10px",
  minHeight: "40px",
  padding: "0 16px",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: 700,
};

const FOOTER_BUTTON_DISABLED_STYLE = {
  ...FOOTER_BUTTON_STYLE,
  background: "#eef1f5",
  color: "#a5aec0",
  cursor: "not-allowed",
};

function SearchGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" width="20" height="20" fill="none">
      <path d="M10.5 18a7.5 7.5 0 1 1 5.3-12.8A7.5 7.5 0 0 1 10.5 18Z" stroke="currentColor" strokeWidth="2" />
      <path d="m16 16 4.5 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function DownloadGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" width="20" height="20" fill="none">
      <path d="M12 3v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="m8 10 4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function RefreshGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" width="20" height="20" fill="none">
      <path d="M20 12a8 8 0 0 0-13.9-5.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M5 5v4h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 12a8 8 0 0 0 13.9 5.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M19 19v-4h-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function OrdersGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" width="32" height="32" fill="none">
      <path d="M6 4h9l3 3v13H6z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M9 10h6M9 14h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M15 4v4h4" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

function BagGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" width="32" height="32" fill="none">
      <path d="M6 8h12l-1 12H7L6 8Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M9 8a3 3 0 0 1 6 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function ClockGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" width="32" height="32" fill="none">
      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="2" />
      <path d="M12 8v5l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function TrendGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" width="18" height="18" fill="none">
      <path d="m6 15 4-4 3 3 5-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M14 8h4v4" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function EyeGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" width="20" height="20" fill="none">
      <path d="M2.5 12s3.5-6.5 9.5-6.5S21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function ChevronDownGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" width="18" height="18" fill="none">
      <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ArrowGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" width="18" height="18" fill="none">
      <path d="M7 17 17 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M9 7h8v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function StatusPill({ status }) {
  const value = String(status || "Reserved").trim() || "Reserved";
  const displayValue = displayStatusLabel(value);
  const lower = value.toLowerCase();
  const theme =
    lower === "on rent"
      ? { background: "#fde9a7", color: "#8d6400" }
      : lower === "return"
        ? { background: "#d8f7db", color: "#157f37" }
        : lower === "return requested"
          ? { background: "#ffe0bf", color: "#b45a00" }
          : { background: "#dce8ff", color: "#1457d9" };

  return <span style={{ ...STATUS_PILL_BASE_STYLE, ...theme }}>{displayValue}</span>;
}

function ModalMark({ source, background, color }) {
  return (
    <div style={{ ...MODAL_HEADER_ICON_WRAP_STYLE, background, color }}>
      <Icon source={source} tone="inherit" />
    </div>
  );
}

function NativeTextInput({ value, onChange, placeholder, icon }) {
  return (
    <div style={{ position: "relative" }}>
      <span
        style={{
          position: "absolute",
          left: "14px",
          top: "50%",
          transform: "translateY(-50%)",
          color: "#6d7b95",
          display: "inline-flex",
          pointerEvents: "none",
        }}
      >
        {icon}
      </span>
      <input
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        style={CONTROL_TEXT_INPUT_STYLE}
      />
    </div>
  );
}

function NativeSelect({ value, onChange, options, ariaLabel }) {
  return (
    <div style={{ position: "relative" }}>
      <select
        aria-label={ariaLabel}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        style={CONTROL_SELECT_STYLE}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export default function RentalOrders({ onNavigate, license }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [savingOrderId, setSavingOrderId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [pageSize, setPageSize] = useState("5");
  const [page, setPage] = useState(1);
  const [draftStatuses, setDraftStatuses] = useState({});
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [quickViewLoading, setQuickViewLoading] = useState(false);
  const [quickViewData, setQuickViewData] = useState(null);
  const [licenseExpired, setLicenseExpired] = useState(false);

  const isLicenseFlaggedExpired =
    Boolean(license?.remainingDays !== undefined && Number(license.remainingDays) <= 0) ||
    Boolean(license?.plan_active === false);

  const isExpired = licenseExpired || isLicenseFlaggedExpired;

  const loadOrders = async (activeRef = { current: true }, { sync = false } = {}) => {
    setLoading(true);
    setError("");

    try {
      if (sync) {
        try {
          await fetch("/api/admin/rental-orders/sync", {
            method: "POST",
            credentials: "include",
            cache: "no-store",
          });
        } catch (_) {
          // ignore background sync errors on initial load
        }
      }

      const response = await fetch("/api/admin/rental-orders", {
        credentials: "include",
        cache: "no-store",
      });
      const data = await response.json();

      if (!response.ok) {
        if (response.status === 403 || data?.isExpired || String(data?.error || "").toLowerCase().includes("license expired")) {
          setLicenseExpired(true);
        }
        throw new Error(data?.message || data?.error || "Failed to load rental orders");
      }

      if (!activeRef.current) return;

      const nextOrders = Array.isArray(data) ? data : [];
      setOrders(nextOrders);
      setDraftStatuses(
        nextOrders.reduce((acc, order) => {
          acc[order.orderId] = order.status || "Reserved";
          return acc;
        }, {}),
      );
    } catch (loadError) {
      if (!activeRef.current) return;
      const errMsg = loadError?.message || "Failed to load rental orders";
      if (errMsg.toLowerCase().includes("license expired") || errMsg.toLowerCase().includes("license")) {
        setLicenseExpired(true);
      }
      setError(errMsg);
    } finally {
      if (activeRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    const activeRef = { current: true };
    loadOrders(activeRef, { sync: true });

    return () => {
      activeRef.current = false;
    };
  }, []);

  const syncHistoricOrders = async () => {
    setSyncing(true);
    setError("");
    setSuccessMessage("");

    try {
      const response = await fetch("/api/admin/rental-orders/sync", {
        method: "POST",
        credentials: "include",
        cache: "no-store",
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Failed to sync older rental orders");
      }

      setSuccessMessage(
        `Synced ${Number(data?.synced) || 0} rental orders from Shopify.`,
      );
      await loadOrders();
    } catch (syncError) {
      setError(syncError?.message || "Failed to sync older rental orders");
    } finally {
      setSyncing(false);
    }
  };

  const filteredOrders = useMemo(() => {
    const query = String(searchTerm || "").trim().toLowerCase();

    return orders.filter((order) => {
      const matchesSearch =
        !query ||
        String(order.orderId || "").toLowerCase().includes(query) ||
        String(order.orderLabel || "").toLowerCase().includes(query) ||
        String(order.productName || "").toLowerCase().includes(query) ||
        String(order.customerName || "").toLowerCase().includes(query);

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "Reserved"
          ? getRentalStageLabel(order) === "Pending Fulfillment"
          : getRentalStageLabel(order) === statusFilter);

      return matchesSearch && matchesStatus;
    });
  }, [orders, searchTerm, statusFilter]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, pageSize]);

  const metrics = useMemo(() => {
    return filteredOrders.reduce(
      (acc, order) => {
        acc.orders += 1;
        acc.quantity += Number(order.totalQuantity ?? order.quantity) || 0;
        return acc;
      },
      { orders: 0, quantity: 0 },
    );
  }, [filteredOrders]);

  const pendingReturnOrders = useMemo(
    () => orders.filter((order) => isPendingReturnOrder(order)),
    [orders],
  );

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / Math.max(1, Number(pageSize) || 5)));
  const pagedOrders = useMemo(() => {
    const size = Math.max(1, Number(pageSize) || 5);
    const startIndex = (page - 1) * size;
    return filteredOrders.slice(startIndex, startIndex + size);
  }, [filteredOrders, page, pageSize]);

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  const exportOrders = () => {
    const rows = filteredOrders.map((order) => ({
      order_id: order.orderId || "",
      order_name: order.orderLabel || "",
      product_name: order.productName || "",
      customer_name: order.customerName || "",
      status: order.status || "",
    }));

    if (!rows.length) return;

    const headers = Object.keys(rows[0]);
    const csv = [
      headers.join(","),
      ...rows.map((row) =>
        headers
          .map((header) => `"${String(row[header] ?? "").replace(/"/g, '""')}"`)
          .join(","),
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "rental-orders.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const openQuickView = async (order) => {
    const orderId = String(order?.orderId || "").trim();
    if (!orderId) return;

    setQuickViewOpen(true);
    setQuickViewLoading(true);
    setQuickViewData({
      orderId,
      orderLabel: order?.orderLabel || `#${orderId}`,
      processedAt: order?.processedAt || order?.createdAt || "",
      productName: order?.productName || "-",
      customerName: order?.customerName || "Guest",
      customerEmail: "",
      customerPhone: "",
      status: order?.status || "Reserved",
      totalQuantity: Number(order?.totalQuantity ?? order?.quantity) || 0,
      returnedQuantity: Number(order?.returnedQuantity ?? order?.returnQuantity) || 0,
      availableQuantity: Number(order?.availableQuantity) || 0,
      returnReason: "",
      rentalLines: [],
      shippingAddressLines: [],
      billingAddressLines: [],
    });
    setError("");

    try {
      const response = await fetch(`/api/admin/rental-orders/${encodeURIComponent(orderId)}`, {
        credentials: "include",
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Failed to load order details");
      }

      setQuickViewData(data);
    } catch (loadError) {
      setError(loadError?.message || "Failed to load order details");
      setQuickViewOpen(false);
    } finally {
      setQuickViewLoading(false);
    }
  };

  const renderAddressLines = (lines = []) => {
    const safeLines = (Array.isArray(lines) ? lines : []).filter(Boolean);
    if (!safeLines.length) {
      return <Text tone="subdued">No address available</Text>;
    }

  return (
      <BlockStack gap="100">
        {safeLines.map((line, index) => {
          const iconSource =
            index === 0 ? PersonIcon : index === 1 ? HomeIcon : index === 2 ? LocationIcon : HashtagIcon;

          return (
            <div key={`${line}-${index}`} style={ADDRESS_LINE_STYLE}>
              <div style={ADDRESS_LINE_ICON_STYLE}>
                <Icon source={iconSource} tone="base" />
              </div>
              <Text>{line}</Text>
            </div>
          );
        })}
      </BlockStack>
    );
  };

  const quickViewTitle = String(quickViewData?.orderLabel || quickViewData?.orderId || "Order Details")
    .replace(/^Order\s*/i, "")
    .trim() || "Order Details";

  const getVisibleQuantity = (status, value, fallback = "-") =>
    isApprovedReturnStatus(status) ? (Number(value) || 0) : fallback;

  const updateOrderStatus = async (orderId) => {
    const nextStatus = draftStatuses[orderId];
    if (!orderId || !nextStatus) return;

    setSavingOrderId(orderId);
    setError("");
    setSuccessMessage("");

    try {
      const response = await fetch(`/api/admin/rental-orders/${encodeURIComponent(orderId)}/status`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: nextStatus }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Failed to update order status");
      }

      setOrders((current) =>
        current.map((order) =>
          order.orderId === orderId
            ? {
                ...order,
                status: nextStatus,
                statusLabel: getRentalStageLabel({
                  ...order,
                  status: nextStatus,
                }),
              }
            : order,
        ),
      );
      setSuccessMessage(`Rental order ${orderId} updated to ${nextStatus}.`);
    } catch (saveError) {
      setError(saveError?.message || "Failed to update rental order");
    } finally {
      setSavingOrderId("");
    }
  };

  return (
    <Page fullWidth>
      {isExpired && (
        <LicenseExpiredOverlay
          onNavigate={onNavigate}
          license={license}
          customMessage="Your NetScore Rental Management license has expired. Access to rental orders and fulfillment status is currently paused."
        />
      )}
      <Box
        style={{
          ...PAGE_SHELL_STYLE,
          filter: isExpired ? "blur(6px)" : "none",
          pointerEvents: isExpired ? "none" : "auto",
          userSelect: isExpired ? "none" : "auto",
          transition: "filter 0.2s ease",
        }}
      >
      <BlockStack gap="400">
        <Box style={DASHBOARD_STACK_STYLE}>
          <Box style={HERO_STYLE}>
            <BlockStack gap="400" style={HERO_INNER_STYLE}>
              <div style={HERO_TOP_STYLE}>
                <div style={HERO_COPY_STYLE}>
                  <div style={{ fontSize: "40px", fontWeight: 800, lineHeight: 1.05, color: "#ffffff" }}>
                    Rental Orders
                  </div>
                  <div style={{ color: "rgba(255,255,255,0.82)", fontSize: "18px", lineHeight: 1.35 }}>
                    View and manage all rental orders.
                  </div>
                </div>

                <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={exportOrders}
                    disabled={!filteredOrders.length}
                    style={HERO_ACTION_BUTTON_STYLE}
                  >
                    <span style={{ display: "inline-flex", marginRight: "10px", verticalAlign: "middle" }}>
                      <DownloadGlyph />
                    </span>
                    Export
                  </button>
                  <button
                    type="button"
                    onClick={syncHistoricOrders}
                    disabled={loading || syncing}
                    style={HERO_REFRESH_BUTTON_STYLE}
                  >
                    <span style={{ display: "inline-flex", marginRight: "10px", verticalAlign: "middle" }}>
                      <RefreshGlyph />
                    </span>
                    {syncing ? "Refreshing..." : "Refresh"}
                  </button>
                </div>
              </div>

              <Box style={CONTROL_BAR_STYLE}>
                <div style={FILTER_ROW_STYLE}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(0, 1fr) minmax(220px, 220px)",
                      gap: "16px",
                      alignItems: "center",
                    }}
                  >
                    <div style={FIELD_WRAPPER_STYLE}>
                      <NativeTextInput
                        value={searchTerm}
                        onChange={setSearchTerm}
                        placeholder="Search order / product / customer"
                        icon={<SearchGlyph />}
                      />
                    </div>
                    <div style={FIELD_WRAPPER_STYLE}>
                      <NativeSelect
                        ariaLabel="Status filter"
                        options={FILTER_STATUS_OPTIONS}
                        value={statusFilter}
                        onChange={setStatusFilter}
                      />
                    </div>
                  </div>

                  <div style={FILTER_META_STYLE}>
                    <span style={{ ...HERO_CHIP_STYLE, background: "#e9f0ff", color: "#1554d3" }}>
                      {metrics.orders} Orders
                    </span>
                    <span style={{ ...HERO_CHIP_STYLE, background: "#fff0c8", color: "#9a6900" }}>
                      {metrics.quantity} Total Items
                    </span>
                  </div>
                </div>
              </Box>

              <Box style={SUMMARY_GRID_STYLE}>
                <Box style={SUMMARY_TILE_STYLE}>
                  <InlineStack align="space-between" blockAlign="center" wrap={false}>
                    <InlineStack gap="400" blockAlign="center" wrap={false}>
                      <Box style={{ ...KPI_ICON_STYLE, background: "#eaf0ff", color: "#2563eb" }}>
                        <OrdersGlyph />
                      </Box>
                      <div style={KPI_META_STYLE}>
                        <div style={KPI_LABEL_STYLE}>Orders in view</div>
                        <div style={{ ...KPI_VALUE_STYLE, fontSize: "34px", fontWeight: 800, lineHeight: 1 }}>
                          {metrics.orders}
                        </div>
                      </div>
                    </InlineStack>
                    <div style={METRIC_ARROW_STYLE}>
                      <ArrowGlyph />
                    </div>
                  </InlineStack>
                </Box>
                <Box style={SUMMARY_TILE_STYLE}>
                  <InlineStack align="space-between" blockAlign="center" wrap={false}>
                    <InlineStack gap="400" blockAlign="center" wrap={false}>
                      <Box style={{ ...KPI_ICON_STYLE, background: "#e3f9e7", color: "#16a34a" }}>
                        <BagGlyph />
                      </Box>
                      <div style={KPI_META_STYLE}>
                        <div style={KPI_LABEL_STYLE}>Rental items</div>
                        <div style={{ ...KPI_VALUE_STYLE, fontSize: "34px", fontWeight: 800, lineHeight: 1 }}>
                          {metrics.quantity}
                        </div>
                      </div>
                    </InlineStack>
                    <div style={METRIC_ARROW_STYLE}>
                      <ArrowGlyph />
                    </div>
                  </InlineStack>
                </Box>
                <Box style={SUMMARY_TILE_STYLE}>
                  <InlineStack align="space-between" blockAlign="center" wrap={false}>
                    <InlineStack gap="400" blockAlign="center" wrap={false}>
                      <Box style={{ ...KPI_ICON_STYLE, background: "#ffecdc", color: "#f97316" }}>
                        <ClockGlyph />
                      </Box>
                      <div style={KPI_META_STYLE}>
                        <div style={KPI_LABEL_STYLE}>Pending approval</div>
                        <div style={{ ...KPI_VALUE_STYLE, fontSize: "34px", fontWeight: 800, lineHeight: 1 }}>
                          {pendingReturnOrders.length}
                        </div>
                      </div>
                    </InlineStack>
                    <div style={METRIC_ARROW_STYLE}>
                      <ArrowGlyph />
                    </div>
                  </InlineStack>
                </Box>
              </Box>

              {pendingReturnOrders.length > 0 && (
                <Card>
                  <Box padding="300" style={{ ...CARD_SHELL_STYLE, background: "rgba(255,255,255,0.98)" }}>
                    <BlockStack gap="200">
                      <InlineStack align="space-between" blockAlign="center" wrap>
                        <Text variant="headingSm" as="h3">
                          Pending Return Requests
                        </Text>
                        <Badge tone="warning">
                          {pendingReturnOrders.length} Pending Approval
                        </Badge>
                      </InlineStack>
                      <Text tone="subdued">
                        Return requests waiting for admin review are shown here.
                      </Text>
                      <BlockStack gap="200">
                        {pendingReturnOrders.slice(0, 4).map((order) => (
                          <InlineStack
                            key={order.orderId}
                            align="space-between"
                            blockAlign="center"
                            wrap={false}
                          >
                            <BlockStack gap="050">
                              <Text fontWeight="medium">{order.orderLabel || `#${order.orderId}`}</Text>
                              <Text tone="subdued">
                                {order.customerName || "Guest"} • {order.productName || "-"}
                              </Text>
                            </BlockStack>
                            <InlineStack gap="200" blockAlign="center" wrap={false}>
                              <Badge tone="warning">Pending Approval</Badge>
                              <Text tone="subdued">Awaiting admin approval</Text>
                              {onNavigate && (
                                <Button onClick={() => onNavigate("pendingApproval")}>
                                  Review Queue
                                </Button>
                              )}
                            </InlineStack>
                          </InlineStack>
                        ))}
                      </BlockStack>
                    </BlockStack>
                  </Box>
                </Card>
              )}
              {loading && <Text tone="subdued">Loading rental orders...</Text>}
              {!loading && !searchTerm && !orders.length && (
                <Text tone="subdued">No rental orders found yet.</Text>
              )}
              {!loading && !!searchTerm && !filteredOrders.length && (
                <Text tone="subdued">No rental orders match your search.</Text>
              )}
              {!!successMessage && <div style={SUCCESS_MESSAGE_STYLE}>{successMessage}</div>}
              {!!error && !isExpired && <Text tone="critical">{error}</Text>}
              <Box style={LIST_PANEL_STYLE}>
                <Box style={LIST_HEADER_STYLE}>
                  <InlineStack align="space-between" blockAlign="center" wrap={false}>
                    <BlockStack gap="050">
                      <div style={TABLE_HEADING_STYLE}>Rental Orders</div>
                      {!loading && filteredOrders.length > 0 && (
                        <div style={TABLE_NOTE_STYLE}>Showing {filteredOrders.length} matching orders</div>
                      )}
                    </BlockStack>
                    <div style={TABLE_NOTE_STYLE}>
                      Manage order status and open the quick view for full details.
                    </div>
                  </InlineStack>
                </Box>

                <BlockStack gap="0">
                  <Box style={TABLE_HEADER_STYLE}>
                    <div style={{ display: "grid", gridTemplateColumns: TABLE_TEMPLATE, gap: "20px", alignItems: "center" }}>
                      <div style={TABLE_HEADING_STYLE}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                          Order
                          <span style={{ color: "#8f98aa", display: "inline-flex" }}>
                            <ChevronDownGlyph />
                          </span>
                        </span>
                      </div>
                      <div style={TABLE_HEADING_STYLE}>Product Name</div>
                      <div style={TABLE_HEADING_STYLE}>Customer Name</div>
                      <div style={TABLE_HEADING_STYLE}>Status</div>
                      <div style={TABLE_HEADING_STYLE}>Update Status</div>
                      <div style={TABLE_HEADING_STYLE}>Actions</div>
                    </div>
                  </Box>

                  {!loading &&
                    pagedOrders.map((order) => (
                      <Box
                        key={order.orderId}
                        padding="0"
                        borderBlockEndWidth="025"
                        borderColor="border"
                        style={TABLE_ROW_STYLE}
                      >
                        <div style={{ display: "grid", gridTemplateColumns: TABLE_TEMPLATE, gap: "20px", alignItems: "center" }}>
                          <div>
                            <div style={ROW_PRIMARY_STYLE}>{order.orderLabel || `#${order.orderId || "-"}`}</div>
                            <div style={ROW_SECONDARY_STYLE}>{order.orderId || "-"}</div>
                          </div>
                          <div style={TABLE_CELL_TEXT_STYLE}>{order.productName || "-"}</div>
                          <div style={TABLE_CELL_TEXT_STYLE}>{order.customerName || "Guest"}</div>
                          <div>
                            <StatusPill status={getRentalStageLabel(order)} />
                            {getStatusHint(getRentalStageLabel(order)) ? (
                              <div style={{ ...ROW_SECONDARY_STYLE, marginTop: "8px" }}>
                                {getStatusHint(getRentalStageLabel(order))}
                              </div>
                            ) : null}
                          </div>
                          <div>
                            <InlineStack gap="200" wrap={false} blockAlign="center">
                              <Box minWidth="180px">
                                <NativeSelect
                                  ariaLabel={`Update status for ${order.orderLabel || order.orderId}`}
                                  options={EDITABLE_STATUS_OPTIONS}
                                  value={draftStatuses[order.orderId] || order.status || "Reserved"}
                                  onChange={(value) =>
                                    setDraftStatuses((current) => ({
                                      ...current,
                                      [order.orderId]: value,
                                    }))
                                  }
                                />
                              </Box>
                              <button
                                type="button"
                                onClick={() => updateOrderStatus(order.orderId)}
                                disabled={savingOrderId === order.orderId}
                                style={TABLE_BUTTON_STYLE}
                              >
                                Save
                              </button>
                            </InlineStack>
                          </div>
                          <div>
                            <button
                              type="button"
                              aria-label={`Quick view for ${order.orderLabel || order.orderId}`}
                              onClick={() => openQuickView(order)}
                              style={ICON_BUTTON_STYLE}
                            >
                              <EyeGlyph />
                            </button>
                          </div>
                        </div>
                      </Box>
                    ))}

                  {!loading && filteredOrders.length === 0 && (
                    <Box padding="200">
                      <Text tone="subdued">No rental orders available for the selected filters.</Text>
                    </Box>
                  )}

                  {filteredOrders.length > 0 && (
                    <Box style={LIST_FOOTER_STYLE}>
                      <InlineStack align="space-between" blockAlign="center" wrap={false}>
                        <InlineStack gap="200" blockAlign="center" wrap={false}>
                          <div style={{ color: "#66708a", fontSize: "15px" }}>Rows per page:</div>
                          <Box minWidth="92px">
                            <NativeSelect
                              ariaLabel="Rows per page"
                              options={[
                                { label: "5", value: "5" },
                                { label: "10", value: "10" },
                                { label: "25", value: "25" },
                                { label: "50", value: "50" },
                                { label: "100", value: "100" },
                              ]}
                              value={pageSize}
                              onChange={setPageSize}
                            />
                          </Box>
                          <div style={{ color: "#66708a", fontSize: "15px" }}>
                            Page {page} of {totalPages}
                          </div>
                        </InlineStack>
                        <InlineStack gap="200" wrap={false}>
                          <button
                            type="button"
                            onClick={() => setPage((current) => Math.max(1, current - 1))}
                            disabled={page <= 1}
                            style={page <= 1 ? FOOTER_BUTTON_DISABLED_STYLE : FOOTER_BUTTON_STYLE}
                          >
                            Previous
                          </button>
                          <button
                            type="button"
                            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                            disabled={page >= totalPages}
                            style={page >= totalPages ? FOOTER_BUTTON_DISABLED_STYLE : FOOTER_BUTTON_STYLE}
                          >
                            Next
                          </button>
                        </InlineStack>
                      </InlineStack>
                    </Box>
                  )}
                </BlockStack>
              </Box>
            </BlockStack>
          </Box>
        </Box>
      </BlockStack>
      </Box>

      <Modal
        open={quickViewOpen}
        onClose={() => {
          setQuickViewOpen(false);
          setQuickViewData(null);
        }}
        title={quickViewTitle}
        titleHidden
        size="large"
      >
        <Modal.Section>
          {quickViewLoading && <Text tone="subdued">Loading order details...</Text>}

          {!quickViewLoading && quickViewData && (
            <div style={MODAL_CONTENT_STYLE}>
              <div style={MODAL_SECTION_STYLE}>
                <div style={CUSTOMER_SECTION_HEADER_STYLE}>
                  <div style={SECTION_HEADER_STYLE}>
                    <ModalMark source={PersonIcon} background="#e8f0ff" color="#2563eb" />
                    <Text variant="headingSm" as="h3">
                      Customer Information
                    </Text>
                  </div>
                  <Text tone="subdued">{quickViewTitle}</Text>
                </div>
                <div style={CUSTOMER_GRID_STYLE}>
                  <div style={CUSTOMER_CELL_STYLE}>
                    <Text tone="subdued">Customer</Text>
                    <div style={MODAL_SECTION_VALUE_STYLE}>
                      {quickViewData.customerName || "Guest"}
                    </div>
                  </div>
                  <div style={{ ...CUSTOMER_CELL_STYLE, ...CUSTOMER_CELL_DIVIDER_STYLE }}>
                    <Text tone="subdued">Email</Text>
                    <div style={MODAL_SECTION_VALUE_STYLE}>
                      {quickViewData.customerEmail || "-"}
                    </div>
                  </div>
                  <div style={{ ...CUSTOMER_CELL_STYLE, ...CUSTOMER_CELL_DIVIDER_STYLE }}>
                    <Text tone="subdued">Phone</Text>
                    <div style={MODAL_SECTION_VALUE_STYLE}>
                      {quickViewData.customerPhone || "-"}
                    </div>
                  </div>
                </div>
              </div>

              <div style={MODAL_SECTION_STYLE}>
                <div style={SECTION_HEADER_STYLE}>
                  <ModalMark source={PackageIcon} background="#ede9fe" color="#7c3aed" />
                  <Text variant="headingSm" as="h3">
                    Rental Information
                  </Text>
                </div>

                {(quickViewData.rentalLines || []).map((line, index) => {
                  const rentalStage = getRentalStageLabel(line);
                  const itemNumber = line.serialNumber || line.itemNumber || line.selectedItem || "-";
                  const imageUrl = String(
                    line.imageUrl ||
                      line.productImageUrl ||
                      line.image_url ||
                      line.image?.url ||
                      line.image?.src ||
                      line.productImage ||
                      "",
                  ).trim();
                  return (
                    <div
                      key={`${line.title || "rental"}-${index}`}
                      style={RENTAL_ITEM_STYLE}
                    >
                      <div style={RENTAL_ITEM_HEAD_STYLE}>
                        <div style={RENTAL_ITEM_META_STYLE}>
                          <div style={THUMB_STYLE}>
                            {imageUrl ? (
                              <img src={imageUrl} alt="" aria-hidden="true" style={THUMB_IMAGE_STYLE} />
                            ) : (
                              <Icon source={PackageIcon} tone="base" />
                            )}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={RENTAL_TITLE_STYLE}>{line.title || "Rental item"}</div>
                            {itemNumber !== "-" ? (
                              <div style={RENTAL_ITEM_NUMBER_STYLE}>Item Number: {itemNumber}</div>
                            ) : null}
                          </div>
                        </div>
                        <Badge tone={getStatusTone(rentalStage)}>
                          {displayStatusLabel(rentalStage)}
                        </Badge>
                      </div>

                      <div style={METRICS_GRID_STYLE}>
                        {[
                          ["Rental Type", line.rateType || "-"],
                          ["Duration", line.duration || "-"],
                          ["Start Date", formatDate(line.startDate)],
                          ["End Date", formatDate(line.endDate)],
                          ["Total Qty", line.totalQuantity || line.quantity || 0],
                        ].map(([label, value], tileIndex) => (
                          <div
                            key={`${label}-${tileIndex}`}
                            style={{
                              ...METRIC_CELL_STYLE,
                              ...(tileIndex > 0 ? METRIC_CELL_DIVIDER_STYLE : {}),
                            }}
                          >
                            <Text tone="subdued">{label}</Text>
                            <div style={{ ...MODAL_SECTION_VALUE_STYLE, marginTop: "8px", fontSize: "19px" }}>
                              {value}
                            </div>
                          </div>
                        ))}
                      </div>

                      <div style={PRICE_GRID_STYLE}>
                        {[
                          [
                            "Available Qty",
                            isApprovedReturnStatus(line.status)
                              ? Number(line.availableQuantity) || 0
                              : Number(line.totalQuantity || line.quantity) || 0,
                          ],
                          [
                            "Returned Qty",
                            getVisibleQuantity(line.status, line.returnedQuantity || line.returnQuantity),
                          ],
                          ["Rental Price", line.priceText || "-"],
                          [
                            "Total Price",
                            line.totalPriceText ||
                              (line.priceText && line.quantity ? `${line.priceText}` : "-"),
                          ],
                        ].map(([label, value], tileIndex) => (
                          <div
                            key={`${label}-${tileIndex}`}
                            style={{
                              ...METRIC_CELL_STYLE,
                              ...(tileIndex > 0 ? METRIC_CELL_DIVIDER_STYLE : {}),
                            }}
                          >
                            <Text tone="subdued">{label}</Text>
                            <div style={{ ...MODAL_SECTION_VALUE_STYLE, marginTop: "8px", fontSize: "18px" }}>
                              {value}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={ADDRESS_GRID_STYLE}>
                <div style={ADDRESS_BOX_STYLE}>
                  <div style={SECTION_HEADER_STYLE}>
                    <ModalMark source={LocationIcon} background="#ede9fe" color="#7c3aed" />
                    <Text variant="headingSm" as="h3">
                      Shipping Address
                    </Text>
                  </div>
                  <div style={{ marginTop: "10px" }}>{renderAddressLines(quickViewData.shippingAddressLines)}</div>
                </div>
                <div style={ADDRESS_BOX_STYLE}>
                  <div style={SECTION_HEADER_STYLE}>
                    <ModalMark source={CreditCardIcon} background="#e8efff" color="#2563eb" />
                    <Text variant="headingSm" as="h3">
                      Billing Address
                    </Text>
                  </div>
                  <div style={{ marginTop: "10px" }}>{renderAddressLines(quickViewData.billingAddressLines)}</div>
                </div>
              </div>
            </div>
          )}
        </Modal.Section>
      </Modal>
    </Page>
  );
}

